/**
 * Fiscal Printer Service - Web Serial API
 * Thin orchestration layer. Implementation is split across fiscal/ modules.
 *
 * Requires: Chrome 89+ or Edge 89+
 */

import { DEFAULT_PROTOCOL } from './fiscal/constants';
import { textToBytes, escapeXml, calculateChecksum } from './fiscal/encoding';
import {
  isSupported, autoConnect as autoConnectFn, connect as connectFn,
  disconnect as disconnectFn, savePortIndex,
  getConnectionStatus as getConnectionStatusFn, subscribeConnection as subscribeConnectionFn
} from './fiscal/connection';
import {
  readResponse, sendRawBytes, sendXML as sendXMLFn,
  sendCommand as sendCommandFn, sendENQ as sendENQFn
} from './fiscal/transport';
import { mapVatRate, isDepositItem, smartTruncateName, prepareItemNames } from './fiscal/itemHelpers';
import { getStatusXML, printReceiptXML, printReceiptFromContractXML, printDailyReportXML, openDrawerXML, cancelTransactionXML, printNonFiscalDocumentXML } from './fiscal/receiptXML';
// UWAGA: printReceiptESCPOS celowo NIE jest importowane - paragony fiskalne
// idą wylacznie protokolem XML (patrz printReceipt); ESC/POS obsluguje tylko
// dokumenty niefiskalne i raporty.
import { getStatusESCPOS, printDailyReportESCPOS, openDrawerESCPOS, cancelTransactionESCPOS, printPeriodicReportESCPOS, printNonFiscalDocumentESCPOS } from './fiscal/receiptESCPOS';
import { printCouponDocumentViaAgent, printNonFiscalViaAgent } from './fiscal/agentAPI';

class FiscalPrinterService {
  constructor() {
    this.port = null;
    this.connected = false;
    this.encoder = new TextEncoder();
    this.decoder = new TextDecoder('windows-1250');
    this.protocol = DEFAULT_PROTOCOL;

    // Expose low-level helpers as instance methods for legacy call-sites
    this.calculateChecksum = calculateChecksum;
    this.textToBytes = textToBytes;
    this.escapeXml = escapeXml;
    this.mapVatRate = mapVatRate;
    this.isDepositItem = isDepositItem;
    this.smartTruncateName = smartTruncateName;
    this.prepareItemNames = prepareItemNames;

    // Wewnetrzna subskrypcja monitoringu — utrzymuje this.connected/this.port
    // zsynchronizowane z realnym stanem portu (np. po reconnect USB dostajemy
    // NOWY obiekt SerialPort), niezaleznie od tego czy UI cokolwiek subskrybuje.
    try {
      subscribeConnectionFn((status) => {
        this.connected = !!status.connected;
        if (status.connected && status.port) this.port = status.port;
      });
    } catch (_) { /* monitoring moze byc niedostepny (brak Web Serial) */ }
  }

  setProtocol(protocol) {
    this.protocol = protocol;
    // protocol set
  }

  getProtocol() { return this.protocol; }

  isSupported() { return isSupported(); }

  async autoConnect() {
    const result = await autoConnectFn({ connected: this.connected, port: this.port });
    if (result.port !== undefined) {
      this.port = result.port;
      this.connected = result.connected;
    }
    return result;
  }

  async savePortIndex() { return savePortIndex(this.port); }

  /**
   * Aktualny stan polaczenia z monitoringu (heartbeat/USB). Dla UI.
   */
  getConnectionStatus() { return getConnectionStatusFn(); }

  /**
   * Subskrypcja zmian stanu polaczenia. Zwraca funkcje odsubskrybowania.
   * Utrzymuje tez this.connected w zgodzie z realnym stanem portu.
   */
  subscribeConnection(fn) {
    return subscribeConnectionFn((status) => {
      this.connected = !!status.connected;
      // Po reconnect (np. ponowne wpiecie USB) monitoring moze miec NOWY obiekt
      // portu — synchronizuj this.port, inaczej druk poszedlby w stary/martwy port.
      if (status.connected && status.port) {
        this.port = status.port;
      }
      if (typeof fn === 'function') fn(status);
    });
  }

  async connect() {
    const result = await connectFn();
    this.port = result.port;
    this.connected = result.connected;
    return result;
  }

  async disconnect() {
    await disconnectFn(this.port);
    this.connected = false;
    this.port = null;
  }

  async sendXML(xmlString, checkResponse = true) {
    if (!this.connected || !this.port) throw new Error('Drukarka nie jest polaczona');
    return sendXMLFn(this.port, this.decoder, xmlString, checkResponse);
  }

  async sendCommand(commandData, includeChecksum = true) {
    if (!this.connected || !this.port) throw new Error('Drukarka nie jest polaczona');
    return sendCommandFn(this.port, commandData, includeChecksum);
  }

  async sendRawBytes(bytes) {
    if (!this.connected || !this.port) throw new Error('Drukarka nie jest polaczona');
    return sendRawBytes(this.port, bytes);
  }

  async readResponse(timeout = 2000) {
    return readResponse(this.port, timeout);
  }

  async sendENQ() {
    if (!this.connected || !this.port) throw new Error('Drukarka nie jest polaczona');
    return sendENQFn(this.port);
  }

  async getStatus() {
    try {
      if (this.protocol === 'xml') {
        return getStatusXML(this.port, this.decoder);
      }
      return getStatusESCPOS(this.port, sendENQFn);
    } catch (error) {
      return { connected: false, responding: false, error: error.message };
    }
  }

  async openDrawer() {
    try {
      if (this.protocol === 'xml') {
        await openDrawerXML(this.port, this.decoder);
      } else {
        await openDrawerESCPOS(this.port);
      }
      return { success: true, message: 'Szuflada otwarta' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async cancelTransaction() {
    try {
      if (this.protocol === 'xml') {
        await cancelTransactionXML(this.port, this.decoder);
      } else {
        await cancelTransactionESCPOS(this.port);
      }
      await new Promise(r => setTimeout(r, 150));
    } catch (e) {
      // cancel failed or no active transaction
    }
  }

  async printReceipt(receipt) {
    if (this.protocol === 'xml') {
      return printReceiptXML(this.port, this.decoder, receipt);
    }
    // PARAGON FISKALNY NA ESC/POS = ODMOWA (2026-08).
    // Novitus Deon fiskalizuje WYLACZNIE przez protokol XML. Galaz ESC/POS
    // drukowala paragon BEZ rabatow i BEZ kaucji, a transakcja i tak konczyla
    // jako 'niezafiskalizowana' - czyli zly papier i zly status naraz.
    // ESC/POS (receiptESCPOS.js) zostaje wylacznie dla dokumentow
    // niefiskalnych (printNonFiscalDocument, raporty).
    const opis = 'Paragony fiskalne drukuje wylacznie protokol XML (Novitus Deon). '
      + 'Przelacz protokol drukarki na XML w Administracja > Drukarka fiskalna '
      + 'i wydrukuj paragon ponownie z listy transakcji.';
    return {
      printed: 'no', success: false, retryable: false, nieWyslano: true,
      errorCode: 'PROTOKOL_ESCPOS', error: opis, errorMessage: opis,
    };
  }

  /**
   * SCIEZKA KONTRAKTOWA: drukuje paragon zbudowany przez SERWER (kontrakt 1.0).
   * Przegladarka nie liczy niczego - sklada pakiety z gotowych ciagow znakow.
   *
   * Dziala WYLACZNIE na protokole XML. Na ESC/POS odmawia druku zamiast po cichu
   * przejsc na stara sciezke liczaca w przegladarce - to bylby powrot do bledu.
   */
  async printReceiptFromContract(kontrakt) {
    if (this.protocol !== 'xml') {
      const opis = 'Sciezka serwerowa dziala tylko na protokole XML - przelacz protokol drukarki';
      return {
        printed: 'no', success: false, retryable: false, nieWyslano: true,
        errorCode: 'PROTOKOL_NIEZNANY', error: opis, errorMessage: opis,
        transkrypt: [], pakietow: 0, bajtowWyslanych: 0, czasDrukuMs: 0,
        wydrukId: kontrakt?.wydruk_id ?? null,
        payloadHash: kontrakt?.kontrola?.payload_hash,
      };
    }
    return printReceiptFromContractXML(this.port, this.decoder, kontrakt);
  }

  async printDailyReport() {
    try {
      if (this.protocol === 'xml') {
        await printDailyReportXML(this.port, this.decoder);
      } else {
        await printDailyReportESCPOS(this.port);
      }
      return { success: true, message: 'Raport dobowy wydrukowany' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async printPeriodicReport(dateFrom, dateTo) {
    try {
      await printPeriodicReportESCPOS(this.port, dateFrom, dateTo);
      return { success: true, message: 'Raport okresowy wydrukowany' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getPrinterInfo() {
    try {
      const response = await sendCommandFn(this.port, '#v', true);
      return {
        success: true,
        raw: response ? Array.from(response).map(b => b.toString(16)).join(' ') : null
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async printNonFiscalDocument(documentData) {
    try {
      if (this.protocol === 'xml') {
        if (!this.connected || !this.port) {
          try { await this.connect(); } catch (_) {
            return { success: false, error: 'Nie udalo sie polaczyc z drukarką.' };
          }
        }
        return printNonFiscalDocumentXML(this.port, this.decoder, documentData);
      }

      // ESC/POS path
      if (!this.connected || !this.port) {
        try { await this.connect(); } catch (_) {
          return { success: false, error: 'Nie udalo sie polaczyc z drukarką. Wybierz port drukarki.' };
        }
      }
      const status = await this.getStatus();
      return printNonFiscalDocumentESCPOS(this.port, this.decoder, status, documentData);
    } catch (error) {
      console.error('Drukarka: blad drukowania dokumentu niefiskalnego:', error);
      return { success: false, error: error.message };
    }
  }

  async printCouponDocumentViaAgent(documentData) {
    return printCouponDocumentViaAgent(documentData);
  }

  async printNonFiscalViaAgent(title, lines, footer = '') {
    return printNonFiscalViaAgent(title, lines, footer);
  }
}

// Singleton
const fiscalPrinterService = new FiscalPrinterService();

export default fiscalPrinterService;
